import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken } from '@angular/core';

import { HttpService, ResponseType } from '@shared/services/http/http.service';
import { HttpErrorResponse } from '@angular/common/http';
import { UrlService } from '@shared/services/url/url.service';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { IPayload } from '@app/shared/interfaces/pricing.interface';
import { Store } from '@ngxs/store';
import { IUpdatePostCodeDevolutionPayload } from '@app/shared/interfaces/resquests.interface';
import { ITradeGalleryResponse } from '@app/tvf/models/trade-gallery-response';
import {
  IDeallocatedTradesBodyApi,
  IDellocatedTrade,
  IDellocatedTradeResponse,
  ILotCreate,
  ILotEdit,
  IRangeDate,
  IRecertifiers,
  ITradeRecertifierUpdate,
} from '@app/shared/interfaces/lotManager.interface';
import { IGalleryService } from '@app/shared/interfaces';
import { IImeiStepService } from '@app/shared/tokens/imei-step.token';

@Injectable({
  providedIn: 'root',
})
export class TvfRequestsService implements IGalleryService, IImeiStepService {
  constructor(
    private _httpService: HttpService,
    private _urlService: UrlService,
    private _http: HttpClient,
    private _store: Store
  ) {}

  exportTrades(
    idStatusTrade: number | number[],
    idTrade: number,
    slaTotal?: number[],
    screeningStep?: any[],
    date?: Date
  ): Observable<any> {
    return of({});
  }

  getTradesAsync(options: {
    page: number;
    pageSize: number;
    idStatusTrade: number | number[];
    imei?: string;
    idTrade?: string;
    CpfCnpj?: string;
    EntryInvoiceNumber?: string;
    slaTotal?: number[];
    screeningStep?: any[];
    date?: Date;
    urlSource?: string;
  }): Observable<any[]> {
    if (options.page === 0) {
      options.page = 1;
    }

    const payload: any = {
      page: options.page,
      pageSize: options.pageSize,
    };

    if (options.idStatusTrade) {
      payload.idStatusTrade = options.idStatusTrade;
    } else if (options.idTrade) {
      payload.idTrade = options.idTrade;
    } else if (options.CpfCnpj) {
      payload.CpfCnpj = options.CpfCnpj;
    } else if (options.EntryInvoiceNumber) {
      payload.EntryInvoiceNumber = options.EntryInvoiceNumber;
    } else if (options.imei) {
      payload.Imei = options.imei;
    }

    const url = this._urlService.getUrl(options.urlSource, payload);

    return this._httpService
      .get(url, payload)
      .pipe(
        map((response: any) => ({
          ...response,
          tradeOrders: response.tradeOrders,
        }))
      )
      .pipe(catchError((error: any) => throwError(error)));
  }

  getTradeByIdTrade(idTrade: number | string): Observable<any> {
    const urlSource = 'COLLECT_TVF.tradeByIdTrade';
    const url = this._urlService.getUrl(urlSource, { idTrade });
    return this._httpService.get(url, idTrade).pipe(catchError((error: any) => throwError(error)));
  }

  getFilePdf(idTrade: string, idAssignmentTerm: string): Observable<any> {
    const payload = { idTrade, idAssignmentTerm };

    const url = this._urlService.getUrl('COLLECT_TVF.downloadPDF', payload);

    let headers: any = new Headers({ 'Content-Type': 'application/pdf' });
    headers = headers.set('Accept', 'application/pdf');

    return this._http
      .get(url, { headers, responseType: 'blob' as 'json' })
      .pipe(catchError((error: any) => throwError(error)));
  }

  getPricing(): Observable<any> {
    const url = this._urlService.getUrl('RECERTIFICATION.getPricing');

    return this._httpService.get(url, null).pipe(catchError((error: any) => throwError(error)));
  }

  getPricingDetails(idLogic: string): Observable<any> {
    const url = this._urlService.getUrl('RECERTIFICATION.getPricingDetails', { idLogic });

    return this._httpService.get(url, idLogic).pipe(catchError((error: any) => throwError(error)));
  }

  editPricingDetails(idLogic: string, payload: IPayload): Observable<IPayload> {
    const url = this._urlService.getUrl('RECERTIFICATION.editPricingDetails');

    return this._httpService
      .put(`${url}/${idLogic}`, payload)
      .pipe(catchError((error: any) => throwError(error)));
  }

  newAnalysisPrice(payload: any) {
    const urlSource = 'COLLECT_TVF.newAnalysisPrice';
    const url = this._urlService.getUrl(urlSource);

    return this._httpService
      .put(`${url}`, payload)
      .pipe(catchError((error: any) => throwError(error)));
  }

  updatePostCodeDevolution(payload: IUpdatePostCodeDevolutionPayload) {
    const url = this._urlService.getUrl('COLLECT_TVF.updatePostCodeDevolution');

    return this._httpService.put(url, payload).pipe(catchError((error: any) => throwError(error)));
  }

  createStatusTrade(tradeId: string, tradeStatusId: number) {
    const url = this._urlService.getUrl('COLLECT_TVF.createStatusTrade', {
      idTrade: tradeId,
      idStatusTrade: tradeStatusId,
    });

    return this._httpService.post(url, {}).pipe(catchError((error: any) => throwError(error)));
  }

  skipTrade(idTradeExternalTrade: string, trackingCode?: number) {
    const urlSource = 'COLLECT_TVF.skipTrade';

    const url = this._urlService.getUrl(urlSource);
    const payload: any = {
      idTrade: idTradeExternalTrade,
    };

    if (trackingCode) {
      payload.trackingCode = trackingCode;
    }

    return this._httpService.post(url, payload).pipe(catchError((error: any) => throwError(error)));
  }

  getCountStatusTrade(idTrade: number): Observable<number> {
    const url = this._urlService.getUrl('COLLECT_TVF.countStatusTrade', { idTrade });
    return this._httpService.get(url, null).pipe(catchError(this.errorMgmt));
  }

  getTradesFilterScreening(filter: string) {
    const url = this._urlService.getUrl('COLLECT_TVF.getTradesFilterScreening', { filter });
    return this._httpService.get(url, null).pipe(catchError(this.errorMgmt));
  }

  // Todo: Teste para exibir progresso no upload
  errorMgmt(error: HttpErrorResponse) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }

    return throwError(() => new Error(errorMessage));
  }

  sendImei(imeiBiped: string, IdTrade: string) {
    const url = this._urlService.getUrl('COLLECT_TVF.sendImei');
    const payload = {
      IdTrade,
      imeiBiped,
    };

    return this._httpService
      .put(url, payload)
      .pipe(catchError((error: any) => throwError(() => new Error(error))));
  }

  saveEvidence(payload: any) {
    const url = this._urlService.getUrl('COLLECT_TVF.saveEvidence');
    return this._httpService.post(url, payload);
  }

  getGalleryByTradeId(idTrade: string): Observable<ITradeGalleryResponse> {
    const urlSource = 'COLLECT_TVF.getTradeGallery';
    const url = this._urlService.getUrl(urlSource, { idTrade });
    return this._httpService.get(url, idTrade);
  }

  closeScreening(idTrade: string, isReappraisal: boolean = false) {
    const url = this._urlService.getUrl('COLLECT_TVF.postCloseScreening');
    return this._httpService.post(url, { idTrade, isReappraisal });
  }

  getTradeTag(idTrade: string) {
    const url = this._urlService.getUrl('COLLECT_TVF.getTradeTag', { idTrade });
    return this._httpService.get(url, idTrade);
  }

  getTradeTagInformations(idTrade: string) {
    const url = this._urlService.getUrl('RECERTIFICATION_TVF.getTradeTagInformations', { idTrade });
    return this._httpService.get(url, idTrade);
  }

  getRecertifiers(): Observable<Array<IRecertifiers>> {
    const url = this._urlService.getUrl('RECERTIFICATION_TVF.getRecertifiers');
    return this._httpService.get(url, null);
  }

  getRecertifiersManualCotationByTrade(idTrade: string): Observable<any> {
    const url = this._urlService.getUrl('COLLECT_TVF.getRecertifiersManualCotationByTrade', {
      idTrade,
    });
    return this._httpService.get(url, null);
  }

  getDellocatedTradesToSaveToLot(
    body: IDeallocatedTradesBodyApi
  ): Observable<IDellocatedTradeResponse> {
    const url = this._urlService.getUrl('COLLECT_TVF.getDellocatedTradesToSaveToLot', {});
    return this._httpService.post(url, body).pipe(catchError((error: any) => throwError(error)));
  }

  getLots(options?: { page: number; pageSize: number; idLote?: number; recertifierName?: string }) {
    const payload: any = {
      page: options?.page || 1,
      pageSize: options?.pageSize || 10,
    };
    if (options?.idLote) {
      payload.idLote = options?.idLote;
    }
    if (options?.recertifierName) {
      payload.recertifierName = options?.recertifierName;
    }
    const url = this._urlService.getUrl('COLLECT_TVF.getLots', payload);

    return this._httpService
      .get(url, payload)
      .pipe(
        map((data: { lots: any; pagination: any }) => ({
          lots: data.lots,
          pagination: data.pagination,
        }))
      );
  }
  getLotById(idLot: number) {
    const url = this._urlService.getUrl('COLLECT_TVF.getLots', {});

    return this._httpService.get(`${url}/${idLot}`, null);
  }

  createLot(body: ILotCreate) {
    const url = this._urlService.getUrl('COLLECT_TVF.getLots', {});
    return this._httpService.post(url, body).pipe(catchError((error: any) => throwError(error)));
  }

  updateTradeRecertifier(body: ITradeRecertifierUpdate) {
    const url = this._urlService.getUrl('RECERTIFICATION_TVF.updateTradeRecertifier', {});
    return this._httpService.put(url, body).pipe(catchError((error: any) => throwError(error)));
  }

  editLot(body: ILotEdit) {
    const url = this._urlService.getUrl('COLLECT_TVF.getLots', {});
    return this._httpService.put(url, body).pipe(catchError((error: any) => throwError(error)));
  }

  exportLotReport(idLot: number) {
    const url = this._urlService.getUrl('COLLECT_TVF.exportLotReport', { idLot });

    return this._httpService.get(url, null, ResponseType.ARRAYBUFFER);
  }

  updateTradeGallery(idTrade: string, filename: string) {
    const payload = { idTrade, filename };
    const url = this._urlService.getUrl('COLLECT_TVF.postRegisterUploadedMedia', {});
    return this._httpService.post(url, payload, ResponseType.JSON);
  }

  revertExtravio(idTrade: string) {
    const url = this._urlService.getUrl('COLLECT_TVF.revertExtravio', {});

    const body = {
      idTrade: idTrade,
    };

    return this._httpService.post(url, body, ResponseType.JSON);
  }
  setAsMisplaced(idTrade: string) {
    const url = this._urlService.getUrl('COLLECT_TVF.setToMisplaced');
    const payload = {
      idTrade,
    };

    return this._httpService.post(url, payload).pipe(catchError((error: any) => throwError(error)));
  }

  isImeiBipedAvailable(imei: string, idtrade: string) {
    const payload = { imei, idtradeExcepts: idtrade };
    const url = this._urlService.getUrl('COLLECT_TVF.getImeiAvailability', payload);
    return this._httpService.get(url, null).pipe(
      catchError((errorRepsonse) => {
        if (errorRepsonse.errors) {
          return throwError(() => new Error(errorRepsonse.errors[0]));
        }
        return throwError(() => errorRepsonse);
      })
    );
  }

  getCountRepproved() {
    const url = this._urlService.getUrl('RECERTIFICATION_TVF.countRepproved');
    return this._httpService.get(url, {});
  }

  getHistoryDetailsTrade(idTrade: string) {
    const payload = { idTrade };
    const url = this._urlService.getUrl('COLLECT_TVF.companyHistory', payload);

    return this._httpService.get(url, {}).pipe(catchError((error: any) => throwError(error)));
  }

  companyDetailsTrade(idTrade: string, body: { carrierPlot: string; agencyCarrierPlot: string }) {
    const payload = { idTrade };

    const url = this._urlService.getUrl('COLLECT_TVF.company', payload);

    return this._httpService.put(url, body).pipe(catchError((error: any) => throwError(error)));
  }

  validateImei(imei: string, idTrade: string): Observable<any> {
    return this.isImeiBipedAvailable(imei, idTrade);
  }

  confirmImei(imei: string, idTrade: string): Observable<any> {
    return this.sendImei(imei, idTrade);
  }
}

export const TVF_SERVICE = new InjectionToken<IGalleryService>('TvfRequestsService');
